import classNames from "classnames";
import { MOBILE_WINDOW_FRAME_FORM_CONTENT_CLASS } from "@shared/ui/overlay/mobile-window-frame";
import type {
    DefineDialogConfig,
    DefineMobileBottomSheetWindowConfig,
    DefineMobileFormWindowConfig,
    DefineMobileWindowConfig,
    DefinedDialog,
    DefinedWindow,
    DefineWindowConfig,
    DialogsApi,
    MobileBottomSheetShellConfig,
    MobileWindowShellConfig,
    OpenDialogOptions,
    OpenWindowOptions,
    WindowsApi,
} from "./types";
import { withMobileBottomSheetShell } from "./mobileBottomSheetShell";
import { withMobileFormWindowHost } from "./mobileFormWindowHost";
import { withMobileWindowShell } from "./mobileWindowShell";

const DEFAULT_WINDOW_OPTIONS: Pick<OpenWindowOptions, "blocking" | "placement" | "reuse"> = {
    blocking: false,
    placement: "fullscreen",
    reuse: "focus-existing",
};

/**
 * Создаёт переиспользуемое window-definition.
 */
export function defineWindow<TProps>(config: DefineWindowConfig<TProps>): DefinedWindow<TProps> {
    const configWithDefaults = {
        ...DEFAULT_WINDOW_OPTIONS,
        ...config,
    };

    return {
        ...configWithDefaults,
        open: (windows: WindowsApi, props: TProps, options?: Partial<Omit<OpenWindowOptions, "ownerId">>) =>
            windows.open(configWithDefaults.component, props, {
                ...configWithDefaults,
                ...options,
            }),
    };
}

function pickMobileWindowShell<TProps extends object>(
    config: DefineMobileWindowConfig<TProps>,
): MobileWindowShellConfig<TProps> {
    const { title, supportingTitle, footer, className, contentClassName, footerClassName } = config;
    return { title, supportingTitle, footer, className, contentClassName, footerClassName };
}

function pickMobileBottomSheetShell<TProps extends object>(
    config: DefineMobileBottomSheetWindowConfig<TProps>,
): MobileBottomSheetShellConfig<TProps> {
    const { title, supportingTitle, footer, className, contentClassName, footerClassName, closeButtonLabel } = config;
    return { title, supportingTitle, footer, className, contentClassName, footerClassName, closeButtonLabel };
}

function pickMobileFormWindowShell<TProps extends object>(
    config: DefineMobileFormWindowConfig<TProps>,
): MobileWindowShellConfig<TProps> {
    const shell = pickMobileWindowShell(config);
    return {
        ...shell,
        contentClassName: classNames(MOBILE_WINDOW_FRAME_FORM_CONTENT_CLASS, shell.contentClassName),
    };
}

/**
 * Создаёт window-definition: content-компонент оборачивается в {@link MobileWindowFrame} при открытии.
 */
export function defineMobileWindow<TProps extends object>(config: DefineMobileWindowConfig<TProps>): DefinedWindow<TProps> {
    const { component, ...windowConfig } = config;
    const shell = pickMobileWindowShell(config);

    return defineWindow<TProps>({
        ...windowConfig,
        component: withMobileWindowShell(component, shell),
    });
}

/**
 * Создаёт bottom-sheet window-definition: modal-панель снизу для коротких контекстных сценариев.
 */
export function defineMobileBottomSheetWindow<TProps extends object>(
    config: DefineMobileBottomSheetWindowConfig<TProps>,
): DefinedWindow<TProps> {
    const { component, ...windowConfig } = config;
    const shell = pickMobileBottomSheetShell(config);

    return defineWindow<TProps>({
        blocking: true,
        placement: "bottom",
        closeOnBackdropClick: true,
        reuse: "replace",
        ...windowConfig,
        component: withMobileBottomSheetShell(component, shell),
    });
}

/**
 * Window-definition для overlay-формы: shell + host (unsaved guard, closeOnSubmit).
 * Контент — {@link FormPanel} / {@link FilterFormPanel} без overlay-пропов.
 */
export function defineMobileFormWindow<TProps extends object>(
    config: DefineMobileFormWindowConfig<TProps>,
): DefinedWindow<TProps> {
    const { component, trackUnsavedFormChanges, closeOnSubmit, ...windowConfig } = config;
    const shell = pickMobileFormWindowShell(config);
    const contentWithHost = withMobileFormWindowHost(component, { trackUnsavedFormChanges, closeOnSubmit });

    return defineWindow<TProps>({
        ...windowConfig,
        component: withMobileWindowShell(contentWithHost, shell),
    });
}

/**
 * Создаёт переиспользуемое dialog-definition.
 */
export function defineDialog<TProps>(config: DefineDialogConfig<TProps>): DefinedDialog<TProps> {
    const configWithDefaults: DefineDialogConfig<TProps> = {
        blocking: true,
        reuse: "focus-existing",
        closeOnEscape: true,
        closeOnBackdropClick: true,
        ...config,
    };

    return {
        ...configWithDefaults,
        open: (dialogs: DialogsApi, props: TProps, options?: Partial<Omit<OpenDialogOptions, "ownerId">>) =>
            dialogs.open(configWithDefaults.component, props, {
                ...configWithDefaults,
                ...options,
            }),
    };
}
